import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { Router } from '@angular/router';
import { AccountActions, EnvironmentActions } from '@app/core/actions';
import * as fromRoot from '@app/reducers';
import { slideInOutAnimation } from '@app/shared/animations/slideInOut-animations';
import { Identity } from '@app/shared/models/identity';
import { Team } from '@app/shared/models/team';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { Constant } from 'src/constant';
import { environment } from 'src/environments/environment';

export interface Subsection {
  title: string;
  path: string;
  displayedPath?: string;
  order?: number;
}
export interface Section {
  name: string;
  path?: string;
  pathMatch: string;
  defaultPath: string;
  subMenuToggled: boolean;
  subsections: Subsection[];
  team?: Team;
}
@Component({
  selector: 'app-side-nav',
  templateUrl: './side-nav.component.html',
  styleUrls: ['./side-nav.component.scss'],
  animations: [
    slideInOutAnimation
  ]
})
export class SideNavComponent implements OnInit, OnChanges, OnDestroy {

  @Input() identity: Identity;
  @Input() isAdministrator: boolean;
  @Input() teams: Team[];
  @Input() teamSelected: Team;
  @Input() planType: 'PREMIUM' | 'TEAM';


  route$: Observable<any>;

  teamSections: Section[] = [];
  adminSections: Section[] = [];

  activeSection: Section;

  subscriptions: Subscription[] = [];
  version: string;

  constructor(
    private readonly router: Router,
    private store$: Store<fromRoot.State>,
  ) {
    this.route$ = this.store$.select(fromRoot.getRoute);
  }

  ngOnInit(): void {
    if (this.isAdministrator) {
      this.computeAdminSections();
    }

    this.listenToSelectedSelection();
    this.version = environment.version;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.teams) {
      this.computeTeamSections();
    }
  }

  computeTeamSections(): void {
    this.teamSections = [];

    this.teams?.forEach(team => {
      const subsections = [];
      if (this.isAdministrator) {
        subsections.push(
          { title: 'TEAM_DASHBOARD_SECTION', path: '/account/team/' + team.id + '/dashboard', displayedPath: '/team/' + team.domain + '/dashboard', order: 1 },
          { title: 'TEAM_TRANSFERS_SENT', path: '/account/team/' + team.id + '/transfers/sent', order: 2 },
          { title: 'TEAM_TRANSFERS_RECEIVED', path: '/account/team/' + team.id + '/transfers/received', order: 2 },
          { title: 'TEAM_CUSTOMIZATION_SECTION', path: '/account/team/' + team.id + '/customization', displayedPath: '/team/' + team.domain + '/customization', order: 4 },
          { title: 'TEAM_PROMOTIONS_SECTION', path: '/account/team/' + team.id + '/promotions', displayedPath: '/team/' + team.domain + '/promotions', order: 5 },
          { title: 'TEAM_SETTINGS_SECTION', path: '/account/team/' + team.id + '/settings', displayedPath: '/team/' + team.domain + '/settings', order: 9 },
        );
      } else if (team?.userRoles?.find(role => role.name.endsWith('$Administrator'))) {
        subsections.push(
          { title: 'TEAM_DASHBOARD_SECTION', path: '/account/team/' + team.id + '/dashboard', displayedPath: '/team/' + team.domain + '/dashboard', order: 1 },
          { title: 'TEAM_TRANSFERS_SENT', path: '/account/team/' + team.id + '/transfers/sent', order: 2 },
          { title: 'TEAM_TRANSFERS_RECEIVED', path: '/account/team/' + team.id + '/transfers/received', order: 2 },
          { title: 'TEAM_CUSTOMIZATION_SECTION', path: '/account/team/' + team.id + '/customization', displayedPath: '/team/' + team.domain + '/customization', order: 4 },
          { title: 'TEAM_PROMOTIONS_SECTION', path: '/account/team/' + team.id + '/promotions', displayedPath: '/team/' + team.domain + '/promotions', order: 5 },
          { title: 'TEAM_MEMBERS_SECTION', path: '/account/team/' + team.id + '/members', displayedPath: '/team/' + team.domain + '/members', order: 6 },
          { title: 'TEAM_GROUPS_SECTION', path: '/account/team/' + team.id + '/groups', displayedPath: '/team/' + team.domain + '/groups', order: 7 },
          { title: 'TEAM_GUESTS_SECTION', path: '/account/team/' + team.id + '/guests', displayedPath: '/team/' + team.domain + '/guests', order: 8 },
          { title: 'TEAM_SETTINGS_SECTION', path: '/account/team/' + team.id + '/settings', displayedPath: '/team/' + team.domain + '/settings', order: 9 },
        );
      } else {
        team?.userRoles?.forEach(role => {
          switch (role.name.split('$')[1]) {
            case Constant.marketerRoleName:
              subsections.push(
                {
                  title: 'TEAM_CUSTOMIZATION_SECTION',
                  path: '/account/team/' + team.id + '/customization',
                  displayedPath: '/team/' + team.domain + '/customization',
                  order: 4
                },
                {
                  title: 'TEAM_PROMOTIONS_SECTION',
                  path: '/account/team/' + team.id + '/promotions',
                  displayedPath: '/team/' + team.domain + '/promotions',
                  order: 5
                }
              );
              break;
            case Constant.reporterRoleName:
              subsections.push(
                {
                  title: 'TEAM_DASHBOARD_SECTION',
                  path: '/account/team/' + team.id + '/dashboard',
                  displayedPath: '/team/' + team.domain + '/dashboard',
                  order: 1
                },
              );
              break;
          }
        });
        subsections.push(
          { title: 'TEAM_TRANSFERS_SENT', path: '/account/team/' + team.id + '/transfers/sent', order: 2 },
          { title: 'TEAM_TRANSFERS_RECEIVED', path: '/account/team/' + team.id + '/transfers/received', order: 2 },
        );
      }
      const orderedSubsections = subsections.sort((a, b) => a.order - b.order);
      this.teamSections.push({
        name: team.name,
        team,
        pathMatch: 'TEAM_SECTIONS',
        subMenuToggled: false,
        defaultPath: orderedSubsections[0].path,
        subsections: orderedSubsections
      });
    });
  }

  computeAdminSections(): void {
    this.adminSections = [];
    if (this.planType === 'PREMIUM') {
      this.adminSections.push({
        name: 'ADMIN_SECTIONS',
        pathMatch: 'ADMIN_SECTIONS',
        subMenuToggled: false,
        defaultPath: '/account/admin/transfers/sent',
        subsections: [
          { title: 'ADMIN_TRANSFERS_SECTION', path: '/account/admin/transfers/sent' },
          { title: 'ADMIN_TRANSFERS_RECEIVED_SECTION', path: '/account/admin/transfers/received' },
          { title: 'ADMIN_TEAMS_SECTION', path: '/account/admin/teams' },
          { title: 'ADMIN_BILLING_SECTION', path: '/account/admin/billing' },
        ]
      });
    } else if (this.planType === 'TEAM') {
      this.adminSections.push({
        name: 'ADMIN_SECTIONS',
        pathMatch: 'ADMIN_SECTIONS',
        defaultPath: '/account/admin/dashboard',
        subMenuToggled: false,
        subsections: [
          { title: 'ADMIN_DASHBOARD_SECTION', path: '/account/admin/dashboard' },
          { title: 'ADMIN_MEMBERS_SECTION', path: '/account/admin/members' },
          { title: 'ADMIN_GUESTS_SECTION', path: '/account/admin/guests' },
          { title: 'ADMIN_GROUPS_SECTION', path: '/account/admin/groups' },
          { title: 'ADMIN_TRANSFERS_SECTION', path: '/account/admin/transfers/sent' },
          { title: 'ADMIN_TRANSFERS_RECEIVED_SECTION', path: '/account/admin/transfers/received' },
          { title: 'ADMIN_TEAMS_SECTION', path: '/account/admin/teams' },
          { title: 'ADMIN_BILLING_SECTION', path: '/account/admin/billing' },
          { title: 'ADMIN_SSO_SECTION', path: '/account/admin/sso' },
          { title: 'ADMIN_PROVISIONING_SECTION', path: '/account/admin/provisioning' },
          { title: 'ADMIN_SECURITY_SECTION', path: '/account/admin/security' },
        ]
      });
    }
  }

  listenToSelectedSelection() {
    this.route$.subscribe(({ currentUrl }) => {

      this.teamSections.map(section => {
        if (section.subsections.find(subsection => currentUrl.startsWith(subsection.path))) {
          section.subMenuToggled = true;
        } else {
          section.subMenuToggled = false;
        }
      });

      this.adminSections.map(section => {
        if (section.subsections.find(subsection => currentUrl.startsWith(subsection.path))) {
          section.subMenuToggled = true;
        } else {
          section.subMenuToggled = false;
        }
      });

    });
  }

  toggleTeamSubsections(section) {
    section.subMenuToggled = !section.subMenuToggled;
    if (section.subMenuToggled) {
      this.store$.dispatch(AccountActions.selectActiveTeam({ team: section.team }));
      this.router.navigateByUrl(section.defaultPath);
    }
  }

  toggleAdminSubsections(section) {
    section.subMenuToggled = !section.subMenuToggled;
    if (section.subMenuToggled) {
      this.router.navigateByUrl(section.defaultPath);
    }
  }

  redirectToLastSelectedTeam() {
    this.store$.dispatch(EnvironmentActions.redirectToSelectedDomain());
  }

  ngOnDestroy() {
    this.subscriptions.map(subscription => {
      subscription.unsubscribe();
    });
    this.subscriptions.length = 0;
  }
}
